import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Paper,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  AccessTime,
} from '@mui/icons-material';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: '400px',
          backgroundImage: 'url(/images/coffee-seeds-leaves.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography
              variant="h1"
              gutterBottom
              sx={{
                fontFamily: '"Bebas Neue", sans-serif',
                fontWeight: 400,
                mb: 2,
              }}
            >
              Contáctanos
            </Typography>
            <Typography
              variant="h5"
              sx={{
                opacity: 0.9,
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.4,
              }}
            >
              Estamos aquí para ayudarte. Envíanos un mensaje y te responderemos pronto.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Content */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                gutterBottom
                sx={{
                  fontFamily: '"Bebas Neue", sans-serif',
                  fontWeight: 400,
                  mb: 3,
                }}
              >
                Ponte en Contacto
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                }}
              >
                ¡Nos encantaría saber de ti! Ya sea que tengas una pregunta sobre nuestro café, 
                quieras hacer una reservación o solo quieras saludar, estamos aquí para ayudarte.
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LocationOn color="primary" sx={{ fontSize: 28 }} />
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Dirección
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Av. Insurgentes Sur 1234<br />
                          Ciudad de México, CDMX 03100
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Phone color="primary" sx={{ fontSize: 28 }} />
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Teléfono
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          +52 (55) 1234-5678
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Email color="primary" sx={{ fontSize: 28 }} />
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Email
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          info@tostocoffee.com
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                <Card sx={{ backgroundColor: 'background.paper' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AccessTime color="primary" sx={{ fontSize: 28 }} />
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          Horarios
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Lunes - Viernes: 7:00 AM - 10:00 PM<br />
                          Sábado - Domingo: 8:00 AM - 11:00 PM
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>

            {/* Contact Form */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                }}
              >
                <Typography
                  variant="h3"
                  gutterBottom
                  sx={{
                    fontFamily: '"Bebas Neue", sans-serif',
                    fontWeight: 400,
                    mb: 3,
                  }}
                >
                  Envíanos un Mensaje
                </Typography>

                {submitted && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    ¡Gracias por tu mensaje! Te responderemos pronto.
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nombre"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Asunto"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        required
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Mensaje"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        multiline
                        rows={4}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        sx={{
                          backgroundColor: 'primary.main',
                          py: 2,
                          fontSize: '1.1rem',
                          '&:hover': {
                            backgroundColor: 'primary.main',
                            opacity: 0.9,
                          },
                        }}
                      >
                        Enviar Mensaje
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default ContactPage; 